import { useCallback, useEffect, useMemo, useState } from "react";
import Seo from "@/components/base/Seo";
import AdminPageHeader from "../components/AdminPageHeader";
import { useAuth } from "@/context/AuthContext";
import {
  getAdminMedia,
  updateAdminMediaItem,
  deleteAdminMediaItem,
  bulkDeleteAdminMedia,
  moveAdminMedia,
} from "@/services/adminApi";
import type { MediaItemPatch, MediaLibraryPage } from "@/types";
import MediaGrid from "./components/MediaGrid";
import MediaDetailsPanel from "./components/MediaDetailsPanel";
import MediaUploader from "./components/MediaUploader";

const PAGE_SIZE = 24;
const MOVE_TARGETS = ["library", "listings", "branding", "reviews", "banners"];

export default function AdminMediaLibrary() {
  const { token } = useAuth();

  const [data, setData] = useState<MediaLibraryPage | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [nonce, setNonce] = useState(0);

  const [search, setSearch] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const [folder, setFolder] = useState("all");
  const [source, setSource] = useState("all");
  const [page, setPage] = useState(1);
  const [view, setView] = useState<"grid" | "list">("grid");

  const [activeId, setActiveId] = useState<string | null>(null);
  const [selectedIds, setSelectedIds] = useState<Set<string>>(new Set());

  const [showUploader, setShowUploader] = useState(false);
  const [saving, setSaving] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [bulkBusy, setBulkBusy] = useState(false);
  const [notice, setNotice] = useState("");
  const [actionError, setActionError] = useState("");

  const flash = (msg: string) => {
    setNotice(msg);
    setTimeout(() => setNotice(""), 2600);
  };
  const flashError = (msg: string) => {
    setActionError(msg);
    setTimeout(() => setActionError(""), 3400);
  };

  // Debounce the search box so we fetch once the admin stops typing.
  useEffect(() => {
    const timer = setTimeout(() => {
      setDebouncedSearch(search);
      setPage(1);
    }, 350);
    return () => clearTimeout(timer);
  }, [search]);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  useEffect(() => {
    let active = true;
    setLoading(true);
    setError(null);
    getAdminMedia({ search: debouncedSearch, folder, source, page, limit: PAGE_SIZE }, token)
      .then((res) => {
        if (active) setData(res);
      })
      .catch((err: unknown) => {
        if (active) setError(err instanceof Error ? err.message : "Something went wrong while loading the library.");
      })
      .finally(() => {
        if (active) setLoading(false);
      });
    return () => {
      active = false;
    };
  }, [token, debouncedSearch, folder, source, page, nonce]);

  const items = data?.items ?? [];
  const activeItem = useMemo(() => items.find((i) => i.id === activeId) ?? null, [items, activeId]);

  const toggleSelect = (id: string) => {
    setSelectedIds((prev) => {
      const next = new Set(prev);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };

  const allOnPageSelected = items.length > 0 && items.every((i) => selectedIds.has(i.id));

  const toggleSelectAll = () => {
    setSelectedIds((prev) => {
      if (allOnPageSelected) {
        const next = new Set(prev);
        items.forEach((i) => next.delete(i.id));
        return next;
      }
      const next = new Set(prev);
      items.forEach((i) => next.add(i.id));
      return next;
    });
  };

  const clearSelection = () => setSelectedIds(new Set());

  const handleUploaded = (uploaded: MediaLibraryPage["items"]) => {
    setData((prev) => {
      if (!prev) return prev;
      return {
        ...prev,
        items: [...uploaded, ...prev.items],
        total: prev.total + uploaded.length,
        stats: { ...prev.stats, total: prev.stats.total + uploaded.length },
      };
    });
    setActiveId(uploaded[0]?.id ?? null);
    flash(`${uploaded.length} file${uploaded.length === 1 ? "" : "s"} uploaded.`);
  };

  const handleSave = async (patch: MediaItemPatch) => {
    if (!activeId) return;
    setSaving(true);
    try {
      const updated = await updateAdminMediaItem(activeId, patch, token);
      setData((prev) =>
        prev ? { ...prev, items: prev.items.map((it) => (it.id === updated.id ? updated : it)) } : prev,
      );
      flash("Details saved.");
    } catch (err) {
      flashError(err instanceof Error ? err.message : "Couldn't save the details. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const removeLocally = (ids: string[]) => {
    setData((prev) => {
      if (!prev) return prev;
      const remove = new Set(ids);
      const kept = prev.items.filter((it) => !remove.has(it.id));
      return { ...prev, items: kept, total: Math.max(0, prev.total - (prev.items.length - kept.length)) };
    });
    setSelectedIds((prev) => {
      const next = new Set(prev);
      ids.forEach((id) => next.delete(id));
      return next;
    });
  };

  const handleDeleteActive = async () => {
    if (!activeId) return;
    setDeleting(true);
    const ok = await deleteAdminMediaItem(activeId, token);
    if (ok) {
      removeLocally([activeId]);
      setActiveId(null);
      flash("Item deleted.");
    } else {
      flashError("Couldn't delete this item. Please try again.");
    }
    setDeleting(false);
  };

  const handleBulkDelete = async () => {
    const ids = Array.from(selectedIds);
    if (ids.length === 0) return;
    setBulkBusy(true);
    try {
      await bulkDeleteAdminMedia(ids, token);
      removeLocally(ids);
      if (activeId && ids.includes(activeId)) setActiveId(null);
      flash(`${ids.length} item${ids.length === 1 ? "" : "s"} deleted.`);
    } catch (err) {
      flashError(err instanceof Error ? err.message : "Couldn't delete the selected items.");
    } finally {
      setBulkBusy(false);
    }
  };

  const handleMove = async (target: string) => {
    const ids = Array.from(selectedIds);
    if (!ids.length || !target) return;
    setBulkBusy(true);
    try {
      await moveAdminMedia(ids, target, token);
      setData((prev) =>
        prev
          ? { ...prev, items: prev.items.map((it) => (ids.includes(it.id) ? { ...it, folder: target } : it)) }
          : prev,
      );
      clearSelection();
      flash(`Moved ${ids.length} item${ids.length === 1 ? "" : "s"} to “${target}”.`);
    } catch (err) {
      flashError(err instanceof Error ? err.message : "Couldn't move the selected items.");
    } finally {
      setBulkBusy(false);
    }
  };

  const totalPages = data ? Math.max(1, Math.ceil(data.total / data.limit)) : 1;
  const folders = data?.folders ?? [];

  return (
    <>
      <Seo title="Media Library" path="/admin/media" />

      <AdminPageHeader
        icon="ri-image-2-line"
        title="Media Library"
        subtitle="Every image uploaded to the site — by owners and admins — stored in /uploads and managed from one place."
        actions={
          actionError ? (
            <span className="inline-flex items-center gap-2 rounded-md border border-secondary-200 bg-secondary-50 px-4 py-2 text-sm text-secondary-900">
              <i className="ri-error-warning-line text-secondary-600"></i> {actionError}
            </span>
          ) : notice ? (
            <span className="inline-flex items-center gap-2 rounded-md border border-accent-200 bg-accent-50 px-4 py-2 text-sm text-accent-900">
              <i className="ri-checkbox-circle-line text-accent-600"></i> {notice}
            </span>
          ) : undefined
        }
      />

      {/* Toolbar */}
      <div className="mt-6 flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
        <div className="flex flex-1 flex-col gap-3 sm:flex-row sm:items-center">
          <div className="relative flex-1 lg:max-w-sm">
            <i className="ri-search-line absolute left-3 top-1/2 -translate-y-1/2 text-foreground-400"></i>
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Search media…"
              className="w-full rounded-md border border-background-300 bg-background-50 py-2 pl-9 pr-3 text-sm text-foreground-900 placeholder:text-foreground-400 focus:border-primary-400 focus:outline-none"
            />
          </div>
          <select
            value={folder}
            onChange={(e) => {
              setFolder(e.target.value);
              setPage(1);
            }}
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none cursor-pointer"
          >
            <option value="all">All folders</option>
            {folders.map((f) => (
              <option key={f.folder} value={f.folder}>
                {f.folder} ({f.count})
              </option>
            ))}
          </select>
          <select
            value={source}
            onChange={(e) => {
              setSource(e.target.value);
              setPage(1);
            }}
            className="rounded-md border border-background-300 bg-background-50 px-3 py-2 text-sm text-foreground-800 focus:border-primary-400 focus:outline-none cursor-pointer"
          >
            <option value="all">All sources</option>
            <option value="admin">Uploaded by admin</option>
            <option value="owner">Uploaded by owner</option>
            <option value="system">System</option>
          </select>
        </div>

        <div className="flex items-center gap-2">
          <div className="flex items-center rounded-md border border-background-300 p-0.5">
            <button
              type="button"
              onClick={() => setView("grid")}
              aria-label="Grid view"
              className={`flex h-8 w-8 items-center justify-center rounded-md cursor-pointer ${
                view === "grid" ? "bg-primary-100 text-primary-700" : "text-foreground-500 hover:bg-background-100"
              }`}
            >
              <i className="ri-grid-fill"></i>
            </button>
            <button
              type="button"
              onClick={() => setView("list")}
              aria-label="List view"
              className={`flex h-8 w-8 items-center justify-center rounded-md cursor-pointer ${
                view === "list" ? "bg-primary-100 text-primary-700" : "text-foreground-500 hover:bg-background-100"
              }`}
            >
              <i className="ri-list-check-2"></i>
            </button>
          </div>
          <button
            type="button"
            onClick={() => setShowUploader(true)}
            className="inline-flex items-center gap-2 rounded-md bg-primary-500 px-4 py-2.5 text-sm font-semibold text-foreground-950 hover:bg-primary-600 whitespace-nowrap cursor-pointer"
          >
            <i className="ri-add-line"></i> Add New
          </button>
        </div>
      </div>

      {/* Bulk bar */}
      <div className="mt-3 flex flex-wrap items-center gap-3">
        <label className="flex items-center gap-2 text-sm text-foreground-600 cursor-pointer">
          <input
            type="checkbox"
            checked={allOnPageSelected}
            onChange={toggleSelectAll}
            className="h-4 w-4 cursor-pointer accent-primary-500"
          />
          Select all
        </label>
        {selectedIds.size > 0 && (
          <div className="flex flex-wrap items-center gap-2 rounded-md border border-background-200 bg-background-100/60 px-3 py-1.5">
            <span className="text-sm font-semibold text-foreground-800">{selectedIds.size} selected</span>
            <select
              disabled={bulkBusy}
              value=""
              onChange={(e) => handleMove(e.target.value)}
              className="rounded-md border border-background-300 bg-background-50 px-2.5 py-1.5 text-sm text-foreground-700 focus:border-primary-400 focus:outline-none cursor-pointer disabled:opacity-50"
            >
              <option value="">Move to…</option>
              {MOVE_TARGETS.map((f) => (
                <option key={f} value={f}>
                  {f}
                </option>
              ))}
            </select>
            <button
              type="button"
              onClick={handleBulkDelete}
              disabled={bulkBusy}
              className="inline-flex items-center gap-1.5 rounded-md border border-secondary-200 px-3 py-1.5 text-sm font-semibold text-secondary-700 hover:bg-secondary-50 disabled:opacity-50 whitespace-nowrap cursor-pointer"
            >
              <i className="ri-delete-bin-line"></i> Delete
            </button>
            <button
              type="button"
              onClick={clearSelection}
              className="text-sm font-medium text-foreground-500 hover:text-foreground-800 cursor-pointer"
            >
              Clear
            </button>
          </div>
        )}
        {data && (
          <span className="ml-auto text-sm text-foreground-500">
            <span className="font-semibold text-foreground-950">{data.total}</span> item
            {data.total === 1 ? "" : "s"}
            {` · ${data.stats.size}`}
          </span>
        )}
      </div>

      <div className="mt-4 grid grid-cols-1 gap-5 lg:grid-cols-[1fr_340px]">
        <div>
          <MediaGrid
            items={items}
            loading={loading}
            error={error}
            view={view}
            activeId={activeId}
            selectedIds={selectedIds}
            onOpen={setActiveId}
            onToggleSelect={toggleSelect}
            onRetry={reload}
          />

          {data && totalPages > 1 && (
            <div className="mt-5 flex items-center justify-center gap-2">
              <button
                type="button"
                onClick={() => setPage((p) => Math.max(1, p - 1))}
                disabled={page <= 1}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:border-primary-400 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
              >
                <i className="ri-arrow-left-s-line text-lg"></i>
              </button>
              <span className="text-sm text-foreground-600">
                Page <span className="font-semibold text-foreground-950">{page}</span> of {totalPages}
              </span>
              <button
                type="button"
                onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                disabled={page >= totalPages}
                className="inline-flex h-9 w-9 items-center justify-center rounded-md border border-background-300 text-foreground-600 hover:border-primary-400 disabled:cursor-not-allowed disabled:opacity-40 cursor-pointer"
              >
                <i className="ri-arrow-right-s-line text-lg"></i>
              </button>
            </div>
          )}
        </div>

        <aside className="lg:sticky lg:top-24 lg:self-start">
          <MediaDetailsPanel
            item={activeItem}
            total={data?.stats.total ?? 0}
            sizeLabel={data?.stats.size ?? "—"}
            folders={folders}
            saving={saving}
            deleting={deleting}
            onSave={handleSave}
            onDelete={handleDeleteActive}
          />
        </aside>
      </div>

      {showUploader && (
        <MediaUploader token={token} onUploaded={handleUploaded} onClose={() => setShowUploader(false)} />
      )}
    </>
  );
}